import React, { useCallback, useMemo, useState } from 'react';
import { Dropdown } from '@/components';
import { useSizes } from '@/utility/responsive.ts';
import Pagination from '@/components/pagination';
import Lightbox from 'yet-another-react-lightbox';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import 'yet-another-react-lightbox/styles.css';
import '@/assets/styles/views/solver/expert-review.scss';
import { useFeatureFlag } from 'configcat-react';
import { useApi } from '@/api/useApi.tsx';
import { useQueryProvider } from '@/hooks';
import { QUERY_KEYS } from '@/constants/QueryKeys.ts';
import { CONFIGCAT_KEYS } from '@/constants/configcat-keys.ts';
import { useAuthStore } from '@/zustand/auth/store.ts';
import { parseJwt, jsonTryParse } from '@/utility';
import { Navigate, useNavigate } from 'react-router';
import { NavigationPath } from '@/router/navigation-path.ts';
import dayjs from 'dayjs';
import { ExpertResolveQuestionResponse, ExpertResolveQuestionResponseQuestionStatusEnum } from '@dopingtech/live_expert_bff_api_ts';
import LoadingSpinner from '@/components/loading-spinner';

interface DropdownOption {
	id: string;
	name: string;
}

const STATUS_OPTIONS: DropdownOption[] = [
	{ id: ExpertResolveQuestionResponseQuestionStatusEnum.InSolution, name: 'Çözülmemiş' },
	{ id: ExpertResolveQuestionResponseQuestionStatusEnum.Solved, name: 'Çözülmüş' },
];

const ITEMS_PER_PAGE = 10;

const formatDate = (date?: string) => (date ? dayjs(date).format('D MMMM YYYY, HH:mm') : '-');

const ResolveQuestionsScreen: React.FC = () => {
	const { isDesktop } = useSizes();
	const { ExpertResolveQuestionControllerApi } = useApi();
	const navigate = useNavigate();

	const { data: authData } = useAuthStore();
	const { value: assignToMeExpertIdListRaw, loading: isLoadingAssignToMeFlag } = useFeatureFlag(CONFIGCAT_KEYS.ASSIGN_TO_ME_EXPERT_ID_LIST, '[]');
	const isActiveAssignToMe = useMemo(() => {
		const expertId = authData?.access_token ? parseJwt(authData.access_token)?.additional_info?.expert_id : null;
		if (!expertId) return false;
		return jsonTryParse(assignToMeExpertIdListRaw as string).some((id: number) => id === parseInt(String(expertId)));
	}, [authData?.access_token, assignToMeExpertIdListRaw]);

	const [currentPage, setCurrentPage] = useState(1);
	const [selectedStatus, setSelectedStatus] = useState<DropdownOption>(STATUS_OPTIONS[0]);

	const [isLightboxOpen, setIsLightboxOpen] = useState(false);
	const [lightboxImage, setLightboxImage] = useState('');

	const isSolved = selectedStatus.id === ExpertResolveQuestionResponseQuestionStatusEnum.Solved;

	const { data: resolveData, isLoading } = useQueryProvider(
		[QUERY_KEYS.listResolveQuestions, selectedStatus.id, currentPage],
		() =>
			ExpertResolveQuestionControllerApi.listResolveQuestions(
				[selectedStatus.id as ExpertResolveQuestionResponseQuestionStatusEnum],
				currentPage - 1,
				ITEMS_PER_PAGE,
			),
		{
			select: ({ data }) => data.data,
			keepPreviousData: true,
			refetchOnWindowFocus: false,
		}
	);

	const questions: ExpertResolveQuestionResponse[] = resolveData?.content ?? [];
	const totalPages: number = resolveData?.totalPages ?? 0;

	const handleImageClick = useCallback((url: string) => {
		setLightboxImage(url);
		setIsLightboxOpen(true);
	}, []);

	const handleSolveClick = useCallback(
		(question: ExpertResolveQuestionResponse, e?: React.MouseEvent) => {
			e?.stopPropagation();
			if (!question.id || !question.questionId) return;
			navigate(NavigationPath.solverSolve, {
				state: {
					resolveQuestion: {
						id: question.id,
						questionId: question.questionId,
						interactionId: question.interactionId,
						questionImage: question.questionImage,
					},
				},
			});
		},
		[navigate]
	);

	const handleStatusSelect = useCallback((opt: DropdownOption) => {
		setSelectedStatus(opt);
		setCurrentPage(1);
	}, []);

	if (isLoadingAssignToMeFlag) {
		return <LoadingSpinner loadingText="Yükleniyor" />;
	}

	if (!isActiveAssignToMe) {
		return (
			<Navigate
				to={NavigationPath.dashboard}
				replace
			/>
		);
	}

	const renderDesktopRow = (question: ExpertResolveQuestionResponse) => (
		<div
			key={question.id}
			className="er-row er-row--readonly">
			<div className="er-col--id">#{question.questionId}</div>
			<div className="er-col--lesson">{formatDate(question.createdAt)}</div>
			<div className="er-col--unit" />
			<div className="er-col--image">
				{question.questionImage ? (
					<img
						src={question.questionImage}
						alt="Soru görseli"
						className="er-thumb"
						onClick={() => handleImageClick(question.questionImage!)}
					/>
				) : (
					<span className="er-rating-zero">-</span>
				)}
			</div>
			<div className="er-col--video">
				<span className="er-rating-zero">-</span>
			</div>
			<div className="er-col--rating">
				<span className={`er-mobile-card__status ${isSolved ? '--reviewed' : '--pending'}`}>{isSolved ? 'Çözüldü' : 'Bekliyor'}</span>
			</div>
			<div className="er-col--action">
				{!isSolved && (
					<button
						className="er-assign-btn"
						onClick={e => handleSolveClick(question, e)}>
						Çöz
					</button>
				)}
			</div>
		</div>
	);

	const renderMobileCard = (question: ExpertResolveQuestionResponse) => (
		<div
			key={question.id}
			className="er-mobile-card er-mobile-card--readonly">
			<div className="er-mobile-card__header">
				<span className="er-mobile-card__id">#{question.questionId}</span>
				<span className={`er-mobile-card__status ${isSolved ? '--reviewed' : '--pending'}`}>{isSolved ? 'Çözüldü' : 'Bekliyor'}</span>
			</div>
			<div className="er-mobile-card__meta">{formatDate(question.createdAt)}</div>
			<div className="er-mobile-card__media">
				{question.questionImage && (
					<img
						src={question.questionImage}
						alt="Soru görseli"
						className="er-mobile-card__img"
						onClick={() => handleImageClick(question.questionImage!)}
					/>
				)}
			</div>
			{!isSolved && (
				<div className="er-mobile-card__footer">
					<button
						className="er-assign-btn er-mobile-card__assign-btn"
						onClick={e => handleSolveClick(question, e)}>
						Çöz
					</button>
				</div>
			)}
		</div>
	);

	const renderEmptyState = () => {
		if (isLoading) return <div className="er-empty">Yükleniyor...</div>;
		return <div className="er-empty">Gösterilecek soru bulunamadı.</div>;
	};

	return (
		<div className="expert-review">
			<div className="er-header">
				<h1>Yeniden Atanan Sorular</h1>
			</div>

			<div className="er-filters">
				<Dropdown
					options={STATUS_OPTIONS}
					selectedOption={selectedStatus}
					onOptionSelect={handleStatusSelect}
					placeholder="Durum Seçin"
					fullWidth
				/>
			</div>

			{isDesktop ? (
				<div className="er-table">
					<div className="er-table__header">
						<div className="er-col">ID</div>
						<div className="er-col">Atanma Tarihi</div>
						<div className="er-col" />
						<div className="er-col er-col--image">Soru Görseli</div>
						<div className="er-col er-col--video" />
						<div className="er-col er-col--rating">Durum</div>
						<div className="er-col er-col--action">İşlem</div>
					</div>
					<div className="er-table__body">{questions.length > 0 ? questions.map(q => renderDesktopRow(q)) : renderEmptyState()}</div>
				</div>
			) : (
				<div className="er-mobile-list">{questions.length > 0 ? questions.map(q => renderMobileCard(q)) : renderEmptyState()}</div>
			)}

			{totalPages > 1 && (
				<div className="er-pagination">
					<Pagination
						currentPage={currentPage}
						totalPages={totalPages}
						onPageChange={setCurrentPage}
					/>
				</div>
			)}

			<Lightbox
				open={isLightboxOpen}
				close={() => setIsLightboxOpen(false)}
				carousel={{ finite: true }}
				controller={{ closeOnBackdropClick: true }}
				render={{ buttonPrev: () => null, buttonNext: () => null }}
				slides={[{ src: lightboxImage, width: 920, height: 520 }]}
				plugins={[Zoom]}
				zoom={{ maxZoomPixelRatio: 5, doubleTapDelay: 300, doubleClickDelay: 300 }}
				styles={{ root: { '--yarl__portal_zindex': '100000' } as React.CSSProperties }}
			/>
		</div>
	);
};

export default ResolveQuestionsScreen;
